import { useState, useCallback } from 'react'
import { lumi } from '@/lib/lumi'
import type { TemplateField } from '@/lib/recruitment'

export interface InterviewTemplate {
  _id: string
  companyUserId: string
  companyName?: string
  name: string
  category?: string
  description?: string
  active?: boolean
  scaleType?: '10' | '100'
  fields?: TemplateField[]
  competencies?: string[]
  creator?: string
  createdAt: string
  updatedAt: string
}

export function useInterviewTemplates() {
  const [templates, setTemplates] = useState<InterviewTemplate[]>([])
  const [loading, setLoading] = useState(false)

  const fetchByCompany = useCallback(async (companyUserId: string) => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.interview_templates.list({
        filter: { companyUserId },
        sort: { createdAt: -1 },
      })
      setTemplates(list as InterviewTemplate[])
    } catch (e) {
      console.error('Error al cargar plantillas de entrevista:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.interview_templates.list({
        sort: { createdAt: -1 },
      })
      setTemplates(list as InterviewTemplate[])
    } catch (e) {
      console.error('Error al cargar plantillas:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  const createTemplate = useCallback(async (data: Partial<InterviewTemplate>) => {
    const now = new Date().toISOString()
    return lumi.entities.interview_templates.create({
      ...data,
      active: data.active ?? true,
      createdAt: now,
      updatedAt: now,
    })
  }, [])

  const updateTemplate = useCallback(async (id: string, data: Partial<InterviewTemplate>) => {
    return lumi.entities.interview_templates.update(id, {
      ...data,
      updatedAt: new Date().toISOString(),
    })
  }, [])

  const deleteTemplate = useCallback(async (id: string) => {
    return lumi.entities.interview_templates.delete(id)
  }, [])

  return { templates, loading, fetchByCompany, fetchAll, createTemplate, updateTemplate, deleteTemplate }
}
